import React, { useState, useEffect } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useWordsData } from '../../lib/useWordsData';
import { useSpeakAnswer } from '../../lib/useSpeakAnswer';
import { useWordAudio } from '../../lib/useWordAudio';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './WordFormationDiminutive.css';

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';

const WordFormationDiminutive = ({ ageGroup, onBack, onComplete }) => {
  const { data: wordsData, loading: wordsLoading } = useWordsData();
  const { start: startSpeakRecognition } = useSpeakAnswer('WordFormationDiminutive');
  const { play: playWordAudio, stop: stopWordAudio } = useWordAudio();
  const { play: playInstruction } = useInstructionAudio();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [answerLog, setAnswerLog] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [recognizerReady, setRecognizerReady] = useState(false);
  const [isSpoken, setIsSpoken] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [highlightedImage, setHighlightedImage] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [hasSpoken, setHasSpoken] = useState(false);
  const [clickedKey, setClickedKey] = useState(null);
  const [spokenKey, setSpokenKey] = useState(null); 

  const [selectedMode, setSelectedMode] = useState(null);
  const [blinkTarget, setBlinkTarget] = useState('speak');

  const IMAGE_MAP = {
    'стул': ['big_chair', 'small_chair'], 'машина': ['car', 'small_car'],
    'чашка': ['big_cup', 'small_cup'], 'ведро': ['bucket', 'small_bucket'],
    'лист': ['leaf', 'small_leaf'], 'гнездо': ['nest', 'small_nest'],
    'пень': ['stump', 'small_stump']
  };

  const WORDS_BY_AGE = {
    '4': ['стул', 'машина', 'чашка'], '5': ['ведро', 'лист', 'гнездо', 'пень'],
    '6': ['ведро', 'лист', 'гнездо', 'пень'], '7-9': ['ведро', 'лист', 'гнездо', 'пень'],
    '10-12': ['ведро', 'лист', 'гнездо', 'пень'], '13-15': ['ведро', 'лист', 'гнездо', 'пень'],
    '16+': ['ведро', 'лист', 'гнездо', 'пень']
  };

  const shuffleArray = (arr) => {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const generateQuestions = () => {
    if (!wordsData) return [];
    const wordsForAge = WORDS_BY_AGE[ageGroup] || WORDS_BY_AGE['4'];
    return wordsForAge
      .filter(wordKey => wordsData.nouns[wordKey]?.singular && wordsData.nouns[wordKey]?.diminutive)
      .map(wordKey => {
        const wordData = wordsData.nouns[wordKey];
        const imgs = IMAGE_MAP[wordKey] || ['placeholder', 'placeholder'];
        return {
          id: wordKey,
          singular: wordData.singular,
          diminutive: wordData.diminutive,
          singularAudioUrl: wordData.singular_audio_url ?? null,
          diminutiveAudioUrl: wordData.diminutive_audio_url ?? null,
          images: {
            singular: `${S3_BASE_URL}/${imgs[0]}.png`,
            diminutive: `${S3_BASE_URL}/${imgs[1]}.png`
          },
          options: ['singular', 'diminutive']
        };
      });
  };

  const questions = generateQuestions();
  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;

  if (currentQuestion && shuffledOptions.length === 0) {
    setShuffledOptions(shuffleArray(currentQuestion.options));
  }

  useEffect(() => {
    if (selectedMode) return;
    const interval = setInterval(() => {
      setBlinkTarget(prev => (prev === 'speak' ? 'show' : 'speak'));
    }, 700);
    return () => clearInterval(interval);
  }, [selectedMode]);

  const handleModeSelect = (mode) => setSelectedMode(mode);

  const goToNextQuestion = () => {
    setIsSpeaking(false); setHighlightedImage(null); setIsProcessing(false);
    setHasSpoken(false); setClickedKey(null); setSpokenKey(null);
    setTimeout(() => {
      if (currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1); setShuffledOptions([]);
        setIsSpoken(false); setIsListening(false); setIsSpeaking(false); setIsCorrect(null);
      } else {
        setShowResult(true);
      }
    }, 800);
  };

  const handleImageClick = (key) => {
    if (selectedMode !== 'show' || !isSpeaking || isProcessing) return;
    setIsProcessing(true); setClickedKey(key);
    const isAnswerCorrect = key === spokenKey;
    setIsCorrect(isAnswerCorrect); setAnswers(prev => [...prev, isAnswerCorrect]);
    setAnswerLog(prev => [...prev, { word: currentQuestion.singular, mode: 'show', correct: isAnswerCorrect }]);
    setTimeout(() => goToNextQuestion(), 800);
  };

  // ===== НОВАЯ ФУНКЦИЯ: ПОВТОР СЛОВА =====
  const repeatInstruction = () => {
    if (!currentQuestion || !selectedMode) return;
    stopWordAudio();
    if (selectedMode === 'speak') {
      playInstruction('Назови ласково', () => playWordAudio(currentQuestion.singularAudioUrl));
    } else {
      const audioUrl = spokenKey ? currentQuestion[`${spokenKey}AudioUrl`] : currentQuestion.singularAudioUrl;
      playWordAudio(audioUrl);
    }
  };

  const speakWord = () => {
    if (isSpoken || !currentQuestion || hasSpoken) return;
    setHasSpoken(true); setIsSpoken(true); setIsCorrect(null); setClickedKey(null); setRecognizerReady(false);

    let audioUrl;

    if (selectedMode === 'speak') {
      setHighlightedImage('singular');
      audioUrl = currentQuestion.singularAudioUrl;
      // Start the recognizer connecting at the same time as the word audio,
      // instead of only after it finishes, so it's ready by the time the
      // child can answer.
      startSpeakRecognition(
        [
          { key: 'singular', text: currentQuestion.singular },
          { key: 'diminutive', text: currentQuestion.diminutive },
        ],
        {
          timeoutMs: 12000,
          onListening: () => setRecognizerReady(true),
          onResult: ({ matchedKey, said }) => {
            const isAnswerCorrect = matchedKey === 'diminutive';
            setIsCorrect(isAnswerCorrect); setAnswers(prev => [...prev, isAnswerCorrect]);
            setAnswerLog(prev => [...prev, { word: currentQuestion.singular, mode: 'speak', correct: isAnswerCorrect, said }]);
            goToNextQuestion();
          },
        }
      );
    } else {
      setHighlightedImage(null);
      const randomKey = Math.random() < 0.5 ? 'singular' : 'diminutive';
      setSpokenKey(randomKey);
      audioUrl = currentQuestion[`${randomKey}AudioUrl`];
    }

    setIsListening(true);
    const onWordEnd = () => {
      setIsListening(false); setIsSpeaking(true);
      if (selectedMode === 'speak') {
        setHighlightedImage('diminutive');
      }
    };

    if (selectedMode === 'speak') {
      // Озвучиваем "Назови ласково" перед словом — без этого ребёнок не
      // понимал, что от него требуется, просто услышав само слово.
      playInstruction('Назови ласково', () => playWordAudio(audioUrl, onWordEnd));
    } else {
      playWordAudio(audioUrl, onWordEnd);
    }
  };

  useEffect(() => {
    if (selectedMode && currentQuestion && !showResult && !isSpoken && !hasSpoken) {
      const timer = setTimeout(() => speakWord(), 500);
      return () => clearTimeout(timer);
    }
  }, [currentQuestionIndex, selectedMode, showResult]);

  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        const correctCount = answers.filter((a) => a === true).length;
        const score = Math.round((correctCount / totalQuestions) * 100);

        const failedWords = [...new Set(answerLog.filter(a => !a.correct).map(a => a.word))];
        const hasUnverifiedSpeakAnswers = answerLog.some(a => a.mode === 'speak');

        if (onComplete) {
          onComplete({
            testId: 'WordFormationDiminutive',
            score: score,
            details: { total: totalQuestions, correct: correctCount, failedWords, mode: selectedMode, hasUnverifiedSpeakAnswers },
          });
        } else {
          onBack();
        }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showResult]);

  if (showResult) {
    const correctCount = answers.filter((a) => a === true).length;
    return (
      <ModuleTemplate 
        progress={answers} 
        totalQuestions={totalQuestions} 
        forceStop={true}
        onComplete={onComplete || onBack}
      >
        <div className="result-content">
          <h2 className="result-title">🎉 Упражнение завершено!</h2>
          <div className="result-score">
            <span className="score-number">{correctCount}</span>
            <span className="score-total"> / {totalQuestions}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (wordsLoading) return (
    <ModuleTemplate
      progress={answers}
      totalQuestions={totalQuestions}
      forceStop={true}
      onComplete={onComplete || onBack}
    >
      <div className="loading-text">Загрузка...</div>
    </ModuleTemplate>
  );

  if (!currentQuestion) return (
    <ModuleTemplate
      progress={answers}
      totalQuestions={totalQuestions}
      forceStop={true}
      onComplete={onComplete || onBack}
    >
      <div className="loading-text">Выберите режим ниже, чтобы начать</div>
    </ModuleTemplate>
  );

  const displayOptions = shuffledOptions.length > 0 ? shuffledOptions : currentQuestion.options;
  const correctKey = selectedMode === 'speak' ? 'diminutive' : spokenKey;

  return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalQuestions} 
      forceStop={true} 
      onComplete={onComplete}      
      onRepeat={repeatInstruction}    
      extraContent="Назови ласково" 
      disabled={!selectedMode}
    >
      <div className="wordformation-container">
        {!selectedMode && (
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '30px' }}>
            <button onClick={() => handleModeSelect('speak')} style={{ padding: '14px 28px', borderRadius: '50px', border: `2px solid ${blinkTarget === 'speak' ? '#7C3AED' : '#E5E0F5'}`, background: blinkTarget === 'speak' ? '#EDE9FE' : '#F8F6FF', color: '#1A1A2E', fontWeight: '600', cursor: 'pointer', fontFamily: 'Inter, sans-serif', boxShadow: blinkTarget === 'speak' ? '0 4px 20px rgba(124, 58, 237, 0.35)' : '0 4px 15px rgba(0,0,0,0.05)', fontSize: '18px', transition: 'all 0.3s' }}>Говорить</button>
            <button onClick={() => handleModeSelect('show')} style={{ padding: '14px 28px', borderRadius: '50px', border: `2px solid ${blinkTarget === 'show' ? '#7C3AED' : '#E5E0F5'}`, background: blinkTarget === 'show' ? '#EDE9FE' : '#F8F6FF', color: '#1A1A2E', fontWeight: '600', cursor: 'pointer', fontFamily: 'Inter, sans-serif', boxShadow: blinkTarget === 'show' ? '0 4px 20px rgba(124, 58, 237, 0.35)' : '0 4px 15px rgba(0,0,0,0.05)', fontSize: '18px', transition: 'all 0.3s' }}>Показать</button>
          </div>
        )}
        <div className="wordformation-main-row">
          <div className="wordformation-images-grid">
            {displayOptions.map((key) => {
              const isHighlighted = highlightedImage === key;
              const isAnswerCorrect = isCorrect === true && key === correctKey;
              const isAnswerWrong = isCorrect === false && (selectedMode === 'show' ? key === clickedKey : key === correctKey);
              return (
                <div key={key} className={`wordformation-image-wrapper ${isHighlighted ? 'highlighted' : ''} ${isAnswerCorrect ? 'answer-correct' : ''} ${isAnswerWrong ? 'answer-wrong' : ''}`}
                  onClick={() => handleImageClick(key)}
                  style={{ cursor: selectedMode === 'show' ? 'pointer' : 'default' }}
                >
                  <img src={currentQuestion.images[key]} alt="" className="wordformation-image" onError={(e) => { e.target.src = '/placeholder.png'; }} />
                </div>
              );
            })}
          </div>
          <div className="wordformation-indicators">
            <div className={`indicator listen ${isListening ? 'active' : ''}`}><span className="indicator-icon">🎧</span></div>
            <div className={`indicator speak ${isSpeaking && (selectedMode !== 'speak' || recognizerReady) ? 'active' : ''}`}><span className="indicator-icon">🗣️</span></div>
          </div>
        </div>
      </div>
    </ModuleTemplate>
  );
};

export default WordFormationDiminutive;